import Calendar from 'react-calendar';
import { useContext, useEffect, useMemo, useState } from 'react';
import 'react-calendar/dist/Calendar.css';
import {
    addDays,
    addHours,
    addMinutes,
    addMonths,
    endOfDay,
    endOfMonth,
    isSameMonth,
    startOfDay,
    startOfMonth,
    subDays,
} from 'date-fns';
import { useAvailabilityQuery } from '../../../../lib/SharedQueries.js';
import {
    formatDate,
    formatDateLong,
    formatInternalDate,
    formatInternalDateTime,
    formatTime,
} from '../../../../lib/DateTime.js';
import Loading from '../../../Loading';
import { ChevronRightIcon } from '@heroicons/react/24/outline/index.js';
import Combobox from '../../../Combobox.jsx';
import Toggle from '../../../Toggle.jsx';
import ErrorMessageBlock from '~/components/ErrorMessageBlock.jsx';
import { FormContext } from '~/components/forms/FormContext.jsx';
import useWeekStart from '~/lib/useWeekStart.js';
import MonthlyAvailabilityMiniCal from '~/components/forms/questions/scheduling/MonthlyAvailabilityMiniCal.jsx';
import {
    clientFormatDateLong,
    clientFormatDateTimeLong,
} from '~/public/lib/ClientDateTime.js';
import BookingTimeZone from '~/components/forms/questions/scheduling/BookingTimeZone.jsx';
import { FormPart, FormStateName, part, state } from '~/lib/FormCssApi';

function calculateStartFromDate(activeStartDate) {
    return subDays(startOfMonth(activeStartDate), 6);
}

function calculateEndFromDate(activeStartDate) {
    return addDays(endOfMonth(activeStartDate), 6);
}

const TimeOption = ({ time, onClick, isSelected, hideSpotsLeft }) => {
    const spotsLeft = time.quantity;
    const showSpotsLeft = !hideSpotsLeft && time.max_quantity > 1;

    return (
        <button
            type="button"
            {...part(FormPart.TIME_OPTION)}
            {...state(isSelected && FormStateName.SELECTED)}
            className={`time-option ${isSelected ? 'time-option-selected' : ''}`}
            onClick={onClick}
        >
            {time.timeLabel}
            {showSpotsLeft && (
                <div className="text-sm">
                    {spotsLeft} spot{spotsLeft > 1 ? 's' : ''} left
                </div>
            )}
        </button>
    );
};

const CustomTimeSelection = ({
    value,
    onChange,
    date,
    times,
    hideSpotsLeft,
}) => {
    const [localValue, setLocalValue] = useState(value);
    const timeOptions = []; //label,value,description
    let current = startOfDay(date);
    const end = endOfDay(date);
    do {
        const isOutSideHours = !times.find(t => {
            return new Date(t.time).getTime() === current.getTime();
        });
        timeOptions.push({
            label: formatTime(current),
            key: current.toISOString(),
            description: isOutSideHours && (
                <span className="text-right float-end">
                    Not shown to clients
                </span>
            ),
        });
        current = addMinutes(current, 5);
    } while (current < end);

    return (
        <Combobox
            options={timeOptions}
            value={localValue}
            onChange={newValue => {
                setLocalValue(newValue);
                if (newValue) onChange(newValue);
            }}
        />
    );
};

const SelectTime = ({
    value,
    onChange,
    selectedDate,
    clientTz,
    times,
    settings,
    allowCustomTime,
    currentSelectedTimes,
}) => {
    // let groupedTimes = {};
    //
    // for (let time of times) {
    //     groupedTimes[time.timeGroup] = [
    //         ...(groupedTimes[time.timeGroup]||[]),
    //         time,
    //     ];
    // }
    const [showCustomTime, setShowCustomTime] = useState(false);

    function isSelected(time) {
        // currentSelectedTimes
        return time === value || currentSelectedTimes.includes(time);
    }

    return (
        <>
            {allowCustomTime && (
                <div className="mb-4 flex justify-end items-center text-sm text-gray-500">
                    Set custom time
                    <Toggle
                        enabled={showCustomTime}
                        setEnabled={setShowCustomTime}
                        className="ml-4"
                    />
                </div>
            )}

            <div {...part(FormPart.TIME_PANEL_HEADING)} className="input-label">
                {clientFormatDateLong(selectedDate, clientTz)}
            </div>

            <div className={showCustomTime ? '' : 'max-h-80 overflow-y-scroll'}>
                {!!settings.isError && (
                    <ErrorMessageBlock className="mb-2">
                        Please select a time below
                    </ErrorMessageBlock>
                )}
                <div
                    {...part(FormPart.TIME_OPTIONS)}
                    className="availability-times__buttons"
                >
                    {showCustomTime && (
                        <CustomTimeSelection
                            value={value}
                            onChange={value => onChange(value, true)}
                            times={times}
                            date={selectedDate}
                        />
                    )}
                    {!showCustomTime && (
                        <>
                            {times.map(time => (
                                <TimeOption
                                    key={time.time}
                                    time={time}
                                    isSelected={isSelected(time.time)}
                                    onClick={() => onChange(time.time)}
                                    hideSpotsLeft={settings['hideSpotsLeft']}
                                />
                            ))}
                            {allowCustomTime && !times?.length && (
                                <div>No availability</div>
                            )}
                        </>
                    )}
                </div>

                {/*{Object.keys(groupedTimes).map((group) => (*/}
                {/*    <div key={group} className="mt-2">*/}
                {/*        <div className="input-label">{group}</div>*/}
                {/*        <div className="">*/}
                {/*            {groupedTimes[group].map((time) => (*/}
                {/*                <TimeOption*/}
                {/*                    key={time.time}*/}
                {/*                    time={time}*/}
                {/*                    isSelected={time.time === value}*/}
                {/*                    onClick={() => onChange(time.time)}*/}
                {/*                />*/}
                {/*            ))}*/}
                {/*        </div>*/}
                {/*    </div>*/}
                {/*))}*/}
            </div>
        </>
    );
};

const ChooseAvailableTime = ({
    value,
    onChange,
    eventTypeId,
    isClass,
    quantity,
    bookableResourceId,
    clientTz,
    settings,
    date,
    currentSelectedTimes,
    extraTime,
}) => {
    const [selectedDate, setSelectedDate] = useState(null);
    const [view, setView] = useState('month');
    const [viewActiveDate, setViewActiveDate] = useState(new Date());
    const [hasJumpedAhead, setHasJumpedAhead] = useState(false);

    const weekStart = useWeekStart();

    const [start, setStart] = useState(calculateStartFromDate(new Date()));
    const [end, setEnd] = useState(calculateEndFromDate(new Date()));

    const opts = {};
    if (settings?.ignoreEvents) {
        opts.ignoreEvents = settings.ignoreEvents;
    }
    if (extraTime) {
        opts.extraTime = extraTime;
    }
    if (settings?.eventTypes?.length > 0) {
        // When combining availability across event types (no specific type
        //  selected) restrict to the types allowed by the question settings
        opts.filterEventTypes = settings.eventTypes;
    }
    if (settings?.stepsOrder === 'personcalendar') {
        // The calendar-first flow can't book series or variable multiday types,
        //  so keep them out of the combined availability too.
        opts.excludeSeriesAndMultiday = true;
    }

    const { data, isLoading } = useAvailabilityQuery(
        eventTypeId,
        bookableResourceId,
        start,
        end,
        clientTz,
        opts,
    );

    const times = (data || []).filter(time => time.quantity >= quantity);
    const hideStartTimes = !!settings?.hideStartTimes;
    const hideDatePicker =
        !!selectedDate && settings.stepsOrder === 'monthlyoverviewevent';

    const timesByDay = {};
    for (const time of times) {
        timesByDay[time.clientDate] = [
            ...(timesByDay[time.clientDate] || []),
            time,
        ];
    }

    const timesForSelectedDate = selectedDate
        ? timesByDay[formatInternalDate(selectedDate, clientTz)] || []
        : [];

    function updateActiveStart(activeStartDate) {
        setStart(calculateStartFromDate(activeStartDate));
        setEnd(calculateEndFromDate(activeStartDate));
        setViewActiveDate(activeStartDate);
    }

    useEffect(() => {
        if (hideStartTimes && selectedDate) {
            // Hiding start times -- pick the date!
            if (timesForSelectedDate?.length) {
                // We're hiding times and have a date w/ availability! Pick the first one
                onChange(timesForSelectedDate[0]?.time);
            } else if (allowCustomTime) {
                // It's an admin, they can reschedule to a date that doesn't have availability
                onChange(selectedDate);
            }
        }
    }, [selectedDate, JSON.stringify(timesForSelectedDate), hideStartTimes]);

    useEffect(() => {
        if (!value && date) {
            setSelectedDate(date);
        }
    }, [date]);

    const isNoAvailability = Object.keys(timesByDay).length === 0;
    const allowCustomTime = !!settings?.allowCustomTime && !isClass;
    const allowCustomDate = !!settings?.allowCustomTime; // For classes can select any date, but not any time

    useEffect(() => {
        if (
            !hideStartTimes &&
            !selectedDate &&
            !date &&
            !value &&
            Object.keys(timesByDay).length > 0
        ) {
            const firstDate = new Date(
                formatInternalDateTime(Object.keys(timesByDay)[0], clientTz),
            );
            // Adjust for time zone
            setSelectedDate(
                addMinutes(firstDate, firstDate.getTimezoneOffset()),
            );
        }

        if (
            selectedDate &&
            !value &&
            !timesByDay[formatInternalDate(selectedDate, clientTz)]?.length
        ) {
            // There's a date selected but no times available, deselect it
            setSelectedDate(null);
        }
    }, [Object.keys(timesByDay).length]);

    useEffect(() => {
        // If there's no availability this month automatically jump to the next month
        if (
            !hasJumpedAhead &&
            !settings?.disableJumpAhead &&
            isNoAvailability &&
            view === 'month' &&
            !isLoading &&
            isSameMonth(viewActiveDate, new Date())
        ) {
            updateActiveStart(addMonths(viewActiveDate, 1));
            setHasJumpedAhead(true);
        }
    }, [isNoAvailability, isLoading, view]);

    const currentSelectedDates = useMemo(() => {
        return currentSelectedTimes.map(st => formatInternalDate(st, clientTz));
    }, [currentSelectedTimes]);

    return (
        <div
            {...part(FormPart.AVAILABILITY)}
            className={`relative bg-white border border-gray-300 rounded-lg p-4 ${!hideStartTimes ? '' : 'max-w-[400px]'}`}
        >
            {isLoading && (
                <Loading className="absolute border z-40 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-4 py-2 rounded-full shadow-lg" />
            )}

            {isNoAvailability && view === 'month' && !isLoading && (
                <div
                    {...part(FormPart.NO_AVAILABILITY)}
                    className={`absolute z-40 text-center cursor-pointer ${allowCustomTime ? 'top-10 left-[20%]' : 'w-[240px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'} border bg-white px-6 py-3 rounded-full shadow-lg`}
                    type="button"
                    onClick={() => {
                        updateActiveStart(addMonths(viewActiveDate, 1));
                    }}
                >
                    No availability this month.
                    <br />
                    <a href="#" onClick={ev => ev.preventDefault()}>
                        View next month ›
                    </a>
                </div>
            )}
            <div
                className={`availability-picker ${isLoading ? 'blur-sm' : ''}`}
            >
                {!hideDatePicker && (
                    <MonthlyAvailabilityMiniCal
                        selectedDate={
                            hideStartTimes
                                ? null /*no start times, don't show this as selected*/
                                : selectedDate
                        }
                        setSelectedDate={setSelectedDate}
                        canSelectAnyDate={allowCustomDate}
                        setView={setView}
                        viewActiveDate={viewActiveDate}
                        updateActiveStart={updateActiveStart}
                        tileDisabled={({ activeStartDate, date, view }) => {
                            if (view !== 'month') {
                                return;
                            }
                            const timesForDate =
                                timesByDay[
                                    formatInternalDate(date, clientTz)
                                ] || [];
                            return (
                                timesForDate.length === 0 && !allowCustomTime
                            );
                        }}
                        tileClassName={({ activeStartDate, date, view }) => {
                            if (
                                currentSelectedDates &&
                                currentSelectedDates.includes(
                                    formatInternalDate(date, clientTz),
                                )
                            ) {
                                return '!text-primary-700 !bg-primary-200 date-option-selected';
                                //return '!text-primary-700 !bg-primary-200 ';
                            }

                            const timesForDate =
                                timesByDay[
                                    formatInternalDate(date, clientTz)
                                ] || [];
                            if (
                                !!allowCustomTime &&
                                timesForDate.length === 0
                            ) {
                                return '!text-gray-500';
                            } else {
                                return null;
                            }
                        }}
                    />

                    //
                    //
                    // <div className="availability-picker__calendar inline-block align-top mb-2">
                    //     <Calendar
                    //         onChange={setSelectedDate}
                    //         goToRangeStartOnSelect={false}
                    //         calendarType={
                    //             weekStart === 0 ? 'gregory' : 'iso8601'
                    //         }
                    //         value={
                    //             hideStartTimes
                    //                 ? null /*no start times, don't show this as selected*/
                    //                 : selectedDate
                    //         }
                    //         showFixedNumberOfWeeks={true}
                    //         minDate={allowCustomTime ? null : new Date()}
                    //         activeStartDate={viewActiveDate}
                    //         onActiveStartDateChange={({
                    //             action,
                    //             activeStartDate,
                    //             value,
                    //             view,
                    //         }) => {
                    //             updateActiveStart(activeStartDate);
                    //             setView(view);
                    //         }}
                    //         tileDisabled={({ activeStartDate, date, view }) => {
                    //             if (view !== 'month') {
                    //                 return;
                    //             }
                    //             const timesForDate =
                    //                 timesByDay[
                    //                     formatInternalDate(date, clientTz)
                    //                 ] || [];
                    //             return (
                    //                 timesForDate.length === 0 &&
                    //                 !allowCustomTime
                    //             );
                    //         }}
                    //         tileClassName={({
                    //             activeStartDate,
                    //             date,
                    //             view,
                    //         }) => {
                    //             if (
                    //                 currentSelectedDates &&
                    //                 currentSelectedDates.includes(
                    //                     formatInternalDate(date, clientTz),
                    //                 )
                    //             ) {
                    //                 return '!text-primary-700 !bg-primary-200 date-option-selected';
                    //                 //return '!text-primary-700 !bg-primary-200 ';
                    //             }
                    //
                    //             const timesForDate =
                    //                 timesByDay[
                    //                     formatInternalDate(date, clientTz)
                    //                 ] || [];
                    //             if (
                    //                 !!allowCustomTime &&
                    //                 timesForDate.length === 0
                    //             ) {
                    //                 return '!text-gray-500';
                    //             } else {
                    //                 return null;
                    //             }
                    //         }}
                    //     />
                    // </div>
                )}
                <div {...part(FormPart.TIME_PANEL)} className="availability-times">
                    <BookingTimeZone clientTz={clientTz} className={hideStartTimes ? 'min-w-48 ml-4 !text-left' : ''} />
                    {!hideStartTimes &&
                        selectedDate &&
                        (timesForSelectedDate.length > 0 ||
                            settings?.allowCustomTime) && (
                            <SelectTime
                                value={value}
                                selectedDate={selectedDate}
                                currentSelectedTimes={currentSelectedTimes}
                                clientTz={clientTz}
                                onChange={(value, isCustom) =>
                                    onChange(value, isCustom)
                                }
                                times={timesForSelectedDate}
                                settings={settings}
                                allowCustomTime={allowCustomTime}
                            />
                        )}
                </div>
            </div>
        </div>
    );
};

export default ChooseAvailableTime;
